import type {
  CellClassParams,
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import { Horizontal, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import type { InvoiceDetail, TransactionDetail } from '../../../../../api/useFreight/types'

import { isDefined, titleCase } from '../../../../../utils/general'
import { ActionsCellRenderer, BookTypeCellRenderer } from '../../../FreightLineItems'
import { SingleProductGroupCellRenderer, StatusCellRenderer } from '../components'
import { LineItemTypeCellRenderer } from '../components'
import { OverrideValueCellRenderer } from '../components/OverrideValueCellRenderer'

/**
 * Returns a standard book type column definition with icon renderer
 */
export const getBookTypeColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: '',
  field: 'book_type',
  maxWidth: 60,
  cellRenderer: ({ value }: ICellRendererParams) => <BookTypeCellRenderer value={value} />,
  cellRendererParams: {
    showTooltip: true,
  },
})

/**
 * Returns a standard counterparty column definition with icon renderer
 */
export const getCounterpartyColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'COUNTERPARTY',
  field: 'counterparty',
  flex: 1,
  cellRenderer: ({ value }: ICellRendererParams) => (
    <Horizontal style={{ overflow: 'visible' }} gap={8} verticalCenter>
      <MaterialIcon icon='store' style={{ color: 'var(--theme-color-2)' }} />
      <Texto>{value || 'N/A'}</Texto>
    </Horizontal>
  ),
})

/**
 * Returns a standard status column definition with status renderer
 */
export const getStatusColumnDef = (hide = false): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'STATUS',
  field: 'status',
  filter: true,
  sortable: true,
  flex: 1.5,
  enableRowGroup: true,
  hide,
  valueFormatter: (params) => titleCase(params.value),
  filterParams: {
    valueFormatter: (params: ValueFormatterParams) => titleCase(params.value),
  },
  cellRenderer: ({ data }: ICellRendererParams) => <StatusCellRenderer data={data} />,
})

export const getOriginsColumnDef = (): ColDef<TransactionDetail | InvoiceDetail> => ({
  headerName: 'ORIGIN',
  field: 'origin_name',
  cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
    return params.data?.origin_name_override !== null
      ? { background: 'var(--theme-warning-dim)' }
      : { background: 'transparent' }
  },
  cellRenderer: OverrideValueCellRenderer,
})

export const getDestinationsColumnDef = (): ColDef<TransactionDetail | InvoiceDetail> => ({
  headerName: 'DESTINATION',
  field: 'destination_name',
  cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
    return params.data?.destination_name_override !== null
      ? { background: 'var(--theme-warning-dim)' }
      : { background: 'transparent' }
  },
  cellRenderer: OverrideValueCellRenderer,
})

export const getBOLColumnDef = (): ColDef<TransactionDetail | InvoiceDetail> => ({
  headerName: 'BOL',
  field: 'bol_number',
  flex: 1,
  cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
    return params.data?.bol_number_override !== null
      ? { background: 'var(--theme-warning-dim)' }
      : { background: 'transparent' }
  },
  cellRenderer: OverrideValueCellRenderer,
})

/**
 * Returns a standard product group column definition
 */
export const getProductGroupColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'PRODUCT',
  field: 'product_group',
  flex: 1,
  cellRenderer: ({ value }: ICellRendererParams) => <SingleProductGroupCellRenderer value={value} />,
})

/**
 * Returns a standard product name column definition
 */
export const getProductNameColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'PRODUCT NAME',
  field: 'product_name',
  flex: 1,
  cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
    return params.data?.product_name_override
      ? { background: 'var(--theme-warning-dim)' }
      : { background: 'transparent' }
  },
  cellRenderer: OverrideValueCellRenderer,
})

/**
 * Returns a standard line item type column definition
 */
export const getLineItemTypeColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'TYPE',
  field: 'type',
  flex: 1,
  minWidth: 135,
  cellRenderer: LineItemTypeCellRenderer,
})

/**
 * Returns a standard subtype column definition
 */
export const getSubtypeColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'SUBTYPE',
  field: 'subtype',
  flex: 1,
  cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
    return params.data?.subtype_override ? { background: 'var(--theme-warning-dim)' } : { background: 'transparent' }
  },
  cellRenderer: OverrideValueCellRenderer,
})

/**
 * Returns a standard rate column definition
 */
export const getRateColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'RATE',
  field: 'rate',
  sortable: true,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  flex: 1,
  cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
    return isDefined(params.data?.rate_override)
      ? { background: 'var(--theme-warning-dim)' }
      : { background: 'transparent' }
  },
  cellRenderer: OverrideValueCellRenderer,
})

/**
 * Returns a standard amount column definition
 */
export const getAmountColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'AMOUNT',
  field: 'amount',
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  flex: 1,
  cellStyle: (params: CellClassParams) => {
    if (!params.data) return null
    return params.data.amount_override !== null
      ? { background: 'var(--theme-warning-dim)' }
      : { background: 'transparent' }
  },
  filterValueGetter: (params: ValueGetterParams<TransactionDetail | InvoiceDetail>) => {
    if (!params.data) return null
    return isDefined(params.data.amount_override) ? params.data.amount_override : params.data.amount
  },
  cellRenderer: OverrideValueCellRenderer,
})

/**
 * Returns a standard UOM column definition
 */
export const getUomColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'UOM',
  field: 'uom',
  flex: 1,
  cellRenderer: OverrideValueCellRenderer,
})

/**
 * Returns a standard total column definition
 */
export const getTotalColumnDef = (): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: 'TOTAL',
  field: 'total',
  pinned: 'right',
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  width: 120,
  flex: 0,
  cellStyle: (params) => {
    if (!params.data) return null
    return params.data.total_override !== null
      ? { background: 'var(--theme-warning-dim)' }
      : { background: 'transparent' }
  },
  cellRenderer: OverrideValueCellRenderer,
  filterValueGetter: (params: ValueGetterParams<TransactionDetail | InvoiceDetail>) => {
    if (!params.data) return null
    return isDefined(params.data.total_override) ? params.data.total_override : params.data.total
  },
})

/**
 * Returns a standard actions column definition
 */
export const getActionsColumnDef = (
  hide = false,
  isCreditRebillMode = false
): Partial<ColDef<TransactionDetail | InvoiceDetail>> => ({
  headerName: '',
  field: 'id',
  sortable: false,
  enableRowGroup: false,
  filter: false,
  pinned: 'right',
  width: 120,
  hide,
  cellStyle: { display: 'flex', flex: 1, padding: 0 },
  //@ts-ignore
  excludeFromExport: true,
  cellRenderer: ({ data, api, context }: ICellRendererParams) => (
    <ActionsCellRenderer data={data} api={api} context={context} isCreditRebillMode={isCreditRebillMode} />
  ),
})
